import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ThemeProvider,
  createTheme,
  CssBaseline
} from '@mui/material';
import logo from '../assets/logo.png';

// Tema específico para a tela de login com as cores padrão
const loginTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1B365D',
    },
    secondary: {
      main: '#78B6CB',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
});

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate('/');
    } catch (error) {
      setError('Falha no login. Verifique suas credenciais.');
    } finally {
      setLoading(false);
    }
  }

  const handleResetPassword = async () => {
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      await resetPassword(resetEmail);
      setSuccess('Email de redefinição de senha enviado! Verifique sua caixa de entrada.');
      setResetDialogOpen(false);
    } catch (error) {
      setError('Erro ao enviar email de redefinição. Verifique se o email está correto.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={loginTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 8,
        }}
      >
        <Container component="main" maxWidth="xs">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <img 
              src={logo} 
              alt="Habitop Logo" 
              style={{ 
                height: '120px',
                width: 'auto',
                marginBottom: '2rem'
              }} 
            />
            <Typography component="h1" variant="h5" color="primary">
              Login
            </Typography>
            
            {error && <Alert severity="error" sx={{ mt: 2, width: '100%' }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mt: 2, width: '100%' }}>{success}</Alert>}
            
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main'
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'primary.main'
                  }
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Senha"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main'
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'primary.main'
                  }
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  }
                }}
                disabled={loading}
              >
                Entrar
              </Button>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Button
                  onClick={() => setResetDialogOpen(true)}
                  variant="text"
                  size="small"
                  sx={{ 
                    textTransform: 'none',
                    color: 'primary.main'
                  }}
                >
                  Esqueci minha senha
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Diálogo de Redefinição de Senha */}
          <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
            <DialogTitle sx={{ color: 'primary.main' }}>
              Redefinir Senha
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 2 }}>
                Digite seu email para receber um link de redefinição de senha.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Email"
                type="email"
                fullWidth
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main'
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'primary.main'
                  }
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setResetDialogOpen(false)}
                sx={{ color: 'text.secondary' }}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleResetPassword} 
                variant="contained"
                disabled={loading}
                sx={{ 
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  }
                }}
              >
                Enviar
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </ThemeProvider>
  );
} 